import { useState } from 'react';

import { Outlet } from 'react-router-dom';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import AppHeader from '@components/navigation/AppHeader';
import AppSidebar from '@components/navigation/AppSidebar';
import themeConfig from '@config/theme.config';
import { DRAWER_WIDTH, MOBILE_DRAWER_MAX_WIDTH } from '@constants/layout';

/**
 * Premium admin shell:
 * - Desktop: permanent sidebar + sticky top bar
 * - Mobile/tablet: temporary drawer + header with menu
 */
function ProtectedLayout() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100%',
        bgcolor: themeConfig.pageBackground,
      }}
    >
      {isDesktop ? (
        <Box
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            height: '100vh',
            zIndex: theme.zIndex.drawer,
          }}
        >
          <AppSidebar variant="permanent" />
        </Box>
      ) : (
        <Drawer
          variant="temporary"
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: `min(88vw, ${MOBILE_DRAWER_MAX_WIDTH}px)`,
              boxSizing: 'border-box',
              border: 'none',
              boxShadow: themeConfig.shadowMd,
            },
            '& .MuiBackdrop-root': {
              bgcolor: 'rgba(15, 23, 42, 0.35)',
            },
          }}
        >
          <AppSidebar variant="temporary" onClose={() => setMenuOpen(false)} />
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <AppHeader showMenuButton={!isDesktop} onMenuClick={() => setMenuOpen(true)} />

        <Box
          component="main"
          sx={{
            flex: 1,
            overflow: 'auto',
            bgcolor: themeConfig.pageBackground,
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

export default ProtectedLayout;
